import { ControlPosition } from "leaflet";
import "../MapView.css";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import { createPortal } from "react-dom";

export type LegendItem = JSX.Element | string;
export interface MapViewLegendProps {
    rows?: LegendItem[];
    onLegendChanged?: (selectedRows: string[]) => void;
    rowsClassName?: string;
    position?: ControlPosition;
    header?: JSX.Element | null;
    map?: L.DrawMap | null;
}

export const Legend = memo((props: MapViewLegendProps) => {
    const rowsClassName = props.rowsClassName || "";
    const position = props.position || "bottomleft";
    const divRef = useRef<HTMLDivElement | null>(null);
    const controlRef = useRef<L.Control | null>(null);
    const [container, setContainer] = useState<HTMLElement | null>(null);

    const wheelListener = useCallback((e: WheelEvent) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
    }, []);

    const dbClickListener = useCallback((e: MouseEvent) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
    }, []);

    useEffect(() => {
        if (!props.map) {
            return;
        }

        // Create a new control if it doesn't exist
        if (!controlRef.current) {
            controlRef.current = new L.Control({ position });

            // Override the onAdd method
            controlRef.current.onAdd = () => {
                const div = L.DomUtil.create("div", "mapView__legend");
                divRef.current = div;
                setContainer(div);

                // Add event listeners
                div.addEventListener("wheel", wheelListener);
                div.addEventListener("dblclick", dbClickListener);

                return div;
            };

            // Add the control to the map
            controlRef.current.addTo(props.map);
        }

        return () => {
            if (props.map && controlRef.current) {
                props.map.removeControl(controlRef.current);
                controlRef.current = null;
                setContainer(null);
            }
            if (divRef.current) {
                divRef.current.removeEventListener("wheel", wheelListener);
                divRef.current.removeEventListener("dblclick", dbClickListener);
            }
        };
    }, [props.map, position, wheelListener, dbClickListener]);

    if (!container || !props.rows || props.rows.length === 0) {
        return null;
    }

    return createPortal(
        <>
            {props.header}
            {props.rows.map((item, index) => (
                <div key={index} className={rowsClassName}>
                    {item}
                </div>
            ))}
        </>,
        container
    );
});
